import * as React from "react";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import { StyledTableHead } from "../../table/BaseTable/BaseTable";
import { Typography } from "@mui/material";
import NoRecordsTableFooter from "../../footer/NoRecordsTableFooter";
import EditableTableHeader from "./EditableTableHeader";
import EditableTableDataRow, {
  EditableTableDataRowWithComparision,
} from "./EditableTableDataRow";
import EditableTableSummaryRow from "./EditableTableSummaryRow";
import { convertBoolenValueToZero } from "../../../helpers/methods";
import FilterTableLoadingRow from "../FilterTable/FilterTableLoadingRow";
import BottomTable from "../../other/BottomTable";

const EditableTable = React.memo((props) => {
  const getDataRowsNumber = React.useCallback(() => {
    return props.data.length;
  }, [props.data, props.countDeletedRows]);

  const getRowDataReadOnly = React.useCallback(
    (editable) => {
      return props.readOnly || !editable;
    },
    [props.readOnly]
  );

  const EditableRow = props.withParamsComparision
    ? EditableTableDataRowWithComparision
    : EditableTableDataRow;

  const colSpan = convertBoolenValueToZero(props.showCheckbox) +
    convertBoolenValueToZero(props.countDataRows) +
    props.tableConfig.length
  return (
    <>
      <TableContainer
        component={Paper}
        style={props.style}
        sx={{ width: "100%" }}
      >
        {props.showTitle && props.title && (
          <Typography variant="h6" color="primary">
            {props.title}
          </Typography>
        )}
        <Table sx={{ width: "100%" }} stickyHeader>
          <StyledTableHead>
            <EditableTableHeader
              showCheckbox={props.showCheckbox}
              checkedAll={props.checkedAll}
              onClickCheckAll={props.onClickCheckAll}
              headersConfig={props.tableConfig}
              countDataRows={props.countDataRows}
            />
          </StyledTableHead>
          <TableBody>
            {props.showTableLoader &&
              <FilterTableLoadingRow
                isLoading={props.isLoading}
                colSpan={colSpan}
              />

            }
            {!props.isLoading && props.data.map((rowData, index) => {
              if (rowData.isDeleted !== true) {
                return (
                  <EditableRow
                    rowIndex={index}
                    showCheckbox={props.showCheckbox}
                    data={rowData.data}
                    id={rowData.id}
                    selected={rowData.selected}
                    onClickCheck={props.onClickCheck}
                    onChange={props.onDataChange}
                    onBlur={props.onBlur}
                    onChangeAutocomplete={props.onChangeAutocomplete}
                    readOnly={getRowDataReadOnly(rowData.editable)}
                    showContextMenu={props.showContextMenu}
                    contextMenuActions={props.contextMenuActions}
                    onClickAddItemToAutocompletedField={
                      props.onClickAddItemToAutocompletedField
                    }
                    allowClickCheckbox={props.allowClickCheckbox}
                    editDate={rowData.editDate}
                    updated={rowData.updated}
                  />
                );
              } else {
                return null;
              }
            })}
            {props.summaryDataRow && !props.isLoading && getDataRowsNumber() > 0 && (
              <EditableTableSummaryRow
                showCheckbox={props.showCheckbox}
                readOnly={props.readOnly}
                data={props.summaryDataRow[0].data}
                fieldConfig={props.fieldConfig}
              />
            )}
            {props.summaryRow && props.summaryRow}
          </TableBody>

          {!props.isLoading && getDataRowsNumber() === 0 && (
            <NoRecordsTableFooter
              colspan={colSpan}
            />
          )}
        </Table>
      </TableContainer>
      <BottomTable
        showExportToFileButton={props.showExportToFileButton}
        showCounterRecords={false} 
        withPagination = {false}
        exportToFileUrl={props.exportToFileUrl}
        exportToFileSearchParams={props.exportToFileSearchParams}
        exportToFileHeaders={props.exportToFileHeaders}
        exportToFileFileName={props.exportToFileFileName}
        exportToFileRequestContent={props.exportToFileRequestContent}
        filterPrefix={props.filterPrefix}
        enableExport={props.enableExport}
      />
    </>
  );
});
export default EditableTable;

EditableTable.propTypes = {
  showCheckbox: PropTypes.bool,
  showPagination: PropTypes.bool,
  checkedAll: PropTypes.bool,
  onClickCheckAll: PropTypes.func,
  onClickCheck: PropTypes.func,
  onClickRow: PropTypes.func,
  data: PropTypes.array,
  headersConfig: PropTypes.object,
  filteringData: PropTypes.object,
  countRecords: PropTypes.number,
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onPageChange: PropTypes.func,
  rowsPerPage: PropTypes.number,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  resetPageNumber: PropTypes.func,
  CollapsibleElement: PropTypes.element,
  selectedItemId: PropTypes.string,
  style: PropTypes.object,
  showTitle: PropTypes.bool,
  title: PropTypes.string,
  filterPrefix: PropTypes.string,
  showCleanFilterIcon: PropTypes.bool,
  onDataChange: PropTypes.func,
  readOnly: PropTypes.bool,
  onChangeAutocomplete: PropTypes.func,
  showContextMenu: PropTypes.bool,
  contextMenuActions: PropTypes.arrayOf(PropTypes.object),
  onClickAddItemToAutocompletedField: PropTypes.func,
  summaryDataRow: PropTypes.obj,
  countDeletedRows: PropTypes.bool,
  countDataRows: PropTypes.bool,
  allowClickCheckbox: PropTypes.bool,
  withParamsComparision: PropTypes.bool,
  showTableLoader: PropTypes.bool,
  isLoading: PropTypes.bool
};

EditableTable.defaultProps = {
  showCheckbox: true,
  showTitle: false,
  title: "",
  showCleanFilterIcon: false,
  showPagination: true,
  readOnly: true,
  showContextMenu: false,
  contextMenuActions: [],
  countDeletedRows: false,
  countDataRows: false,
  allowClickCheckbox: false,
  withParamsComparision: false,
  showTableLoader: false,
  isLoading: false
};
